import { FC, useEffect, useState } from 'react';
import {
  Alert,
  Form,
  Input,
  InputNumber,
  Modal,
  Radio,
  Spin,
  Typography
} from 'antd';
import { createCreditNote, fetchCreditable } from 'services/api/invoicing';
import {
  Invoice,
  InvoicePaymentMethod,
  InvoiceStatus
} from 'types/api/invoicing';

import { showErrorMessage } from 'utils/showErrorMessage';
import { money } from 'utils/price';

interface IProps {
  open: boolean;
  invoice: Invoice;
  onClose: () => void;
  onCreated: (creditNote: Invoice) => void;
}

type FormValues = {
  amount?: number;
  refundViaStripe?: boolean;
  reason?: string;
};

/**
 * Issue a credit note against one invoice. The amount is entered positive and
 * stored negative; several partial notes against one invoice are allowed, so
 * the bound comes from /creditable rather than the invoice total.
 */
const CreditNoteModal: FC<IProps> = ({ open, invoice, onClose, onCreated }) => {
  const [form] = Form.useForm<FormValues>();
  // null = the bound could not be loaded (see loadFailed below), which is not
  // the same as 0 = nothing left to credit.
  const [remaining, setRemaining] = useState<number | null>(null);
  const [loadFailed, setLoadFailed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  // A Stripe refund needs money that actually went through Stripe. The BE
  // rejects anything else, so the choice is disabled rather than offered.
  const canRefund =
    invoice.status === InvoiceStatus.Paid &&
    invoice.paymentMethod === InvoicePaymentMethod.Stripe;

  useEffect(() => {
    if (!open) {
      return;
    }

    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setLoadFailed(false);
        const value = await fetchCreditable(invoice.publicId);

        if (cancelled) return;
        setRemaining(value);
        form.setFieldsValue({ amount: value, refundViaStripe: canRefund });
      } catch {
        // Losing the bound must not block the correction: the BE re-checks the
        // amount and refuses an over-credit with its own message. Fall back to
        // the invoice total and say so.
        if (!cancelled) {
          setRemaining(null);
          setLoadFailed(true);
          form.setFieldsValue({
            amount: Number(invoice.totalAmount),
            refundViaStripe: canRefund
          });
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [open, invoice.publicId, invoice.totalAmount, form, canRefund]);

  const maxAmount = remaining ?? Number(invoice.totalAmount);

  const onFinish = async (values: FormValues) => {
    try {
      setSaving(true);
      const note = await createCreditNote(invoice.publicId, {
        amount: Number(values.amount),
        refundViaStripe: !!values.refundViaStripe,
        reason: values.reason?.trim() || undefined
      });

      // The parent closes and navigates — closing here as well would race
      // with its own state update and leave the modal on screen.
      onCreated(note);
    } catch (err) {
      // The BE explains each refusal (draft, cancelled, over the remaining
      // amount, no Stripe payment) — show its message, don't invent one.
      showErrorMessage(err);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onCancel={onClose}
      onOk={() => form.submit()}
      okText="Issue credit note"
      title={`Credit note against ${invoice.invoiceNumber ?? 'this invoice'}`}
      confirmLoading={saving}
      okButtonProps={{ disabled: loading || maxAmount <= 0 }}
      closable={!saving}
    >
      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: 24 }}>
          <Spin />
        </div>
      ) : (
        <Form form={form} layout="vertical" onFinish={onFinish}>
          {loadFailed && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Could not load the remaining amount."
              description="Showing the invoice total instead. The amount is checked again when the note is issued."
            />
          )}

          {remaining === 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: 16 }}
              message="Nothing left to credit on this invoice."
            />
          )}

          <Typography.Paragraph type="secondary">
            {loadFailed
              ? `Invoice total: ${money(maxAmount, {
                  currency: invoice.currency
                })}`
              : `Still creditable: ${money(maxAmount, {
                  currency: invoice.currency
                })}`}
          </Typography.Paragraph>

          <Form.Item
            label="Amount to credit"
            name="amount"
            rules={[
              { required: true, message: 'Enter an amount' },
              {
                type: 'number',
                min: 0.01,
                max: maxAmount,
                message: `Between 0.01 and ${maxAmount}`
              }
            ]}
          >
            <InputNumber
              style={{ width: '100%' }}
              min={0.01}
              max={maxAmount}
              step={0.01}
              precision={2}
              addonAfter={invoice.currency}
            />
          </Form.Item>

          <Form.Item label="What should happen" name="refundViaStripe">
            <Radio.Group>
              <Radio value disabled={!canRefund}>
                Refund the payer
              </Radio>
              <Radio value={false}>Issue a correction only</Radio>
            </Radio.Group>
          </Form.Item>
          <Typography.Paragraph type="secondary">
            {canRefund
              ? 'A refund sends the money back through Stripe. A correction writes the document only — use it when the money is returned by bank transfer.'
              : 'Only a paid Stripe invoice can be refunded, so this writes the document only.'}
          </Typography.Paragraph>

          <Form.Item label="Reason (optional)" name="reason">
            <Input.TextArea
              rows={2}
              placeholder="Stored on the note and sent to Stripe as the memo"
            />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
};

export default CreditNoteModal;
